/**
 * JourneyTimeline — Vertical chronological care journey for a subject actor.
 *
 * DRR-126 Phase 6: Renders the full past + present care history of a resident
 * (or any subject_actor_id) as a vertical timeline. Each card = one constellation
 * instance (a care event). Active events float to the top.
 *
 * Cards show:
 *   - Status badge (in_progress = pulsing, overdue = red, completed = green)
 *   - Event date range (started_at → last_activity_at)
 *   - Task count + task type badges
 *   - Staff count (no IDs — DRR-128 team attribution)
 *   - Regulated badge when mandate is present (no config ID — DRR-117)
 *   - Link to constellation instance view
 *
 * Data tier: T2. No clinical content. DRR-114 compliant.
 */

import React, { useCallback } from 'react';
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  Title,
} from '@mantine/core';
import type { JourneyTimelineEvent } from './hooks/useSubjectJourneyTimeline';
export type { JourneyTimelineEvent } from './hooks/useSubjectJourneyTimeline';

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  string,
  { color: string; label: string; icon: string }
> = {
  in_progress: { color: 'blue', label: 'Active', icon: '●' },
  overdue: { color: 'red', label: 'Overdue', icon: '⚠' },
  completed: { color: 'green', label: 'Completed', icon: '✓' },
  pending: { color: 'gray', label: 'Pending', icon: '○' },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface JourneyTimelineProps {
  readonly events: JourneyTimelineEvent[];
  readonly activeEvents: JourneyTimelineEvent[];
  readonly historicalEvents: JourneyTimelineEvent[];
  readonly totalEvents: number;
  readonly activeEventCount: number;
  readonly loading?: boolean;
  /** Called when a timeline event card is clicked */
  readonly onEventClick?: (event: JourneyTimelineEvent) => void;
  /** Map of constellation ID → display name */
  readonly constellationNames?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JourneyTimeline({
  events,
  activeEvents,
  historicalEvents,
  totalEvents,
  activeEventCount,
  loading = false,
  onEventClick,
  constellationNames = {},
}: JourneyTimelineProps) {
  const handleEventClick = useCallback(
    (event: JourneyTimelineEvent) => {
      onEventClick?.(event);
    },
    [onEventClick],
  );

  if (loading && events.length === 0) {
    return (
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
        <Stack align="center" gap="sm">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Loading care journey…
          </Text>
        </Stack>
      </Box>
    );
  }

  if (!loading && events.length === 0) {
    return (
      <Box p="xl">
        <Text size="sm" c="dimmed" ta="center">
          No care events found for this subject. Journey begins when the first
          constellation instance is created.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={5}>Care Journey</Title>
        <Group gap="xs">
          {activeEventCount > 0 && (
            <Badge color="blue" variant="dot">
              {activeEventCount} active
            </Badge>
          )}
          <Badge color="gray" variant="light">
            {totalEvents} total events
          </Badge>
        </Group>
      </Group>

      {/* Active events section */}
      {activeEvents.length > 0 && (
        <Stack gap="xs">
          <Text size="xs" tt="uppercase" fw={600} c="blue">
            Active Now
          </Text>
          {activeEvents.map((event) => (
            <EventCard
              key={event.eventId}
              event={event}
              constellationNames={constellationNames}
              onClick={handleEventClick}
              isActive
            />
          ))}
        </Stack>
      )}

      {/* Historical timeline */}
      {historicalEvents.length > 0 && (
        <Stack gap="xs">
          {activeEvents.length > 0 && (
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">
              Past Events
            </Text>
          )}
          <Timeline active={-1} bulletSize={20} lineWidth={2}>
            {historicalEvents.map((event) => {
              const cfg = STATUS_CONFIG[event.status] ?? STATUS_CONFIG['pending'] ?? { color: 'gray', label: 'Pending', icon: '○' };
              return (
                <Timeline.Item
                  key={event.eventId}
                  bullet={
                    <ThemeIcon
                      size={20}
                      radius="xl"
                      color={cfg.color}
                      variant="filled"
                    >
                      <Text size="xs">{cfg.icon}</Text>
                    </ThemeIcon>
                  }
                >
                  <EventCard
                    event={event}
                    constellationNames={constellationNames}
                    onClick={handleEventClick}
                  />
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Stack>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// EventCard
// ---------------------------------------------------------------------------

interface EventCardProps {
  readonly event: JourneyTimelineEvent;
  readonly constellationNames: Record<string, string>;
  readonly onClick: (event: JourneyTimelineEvent) => void;
  readonly isActive?: boolean;
}

function EventCard({ event, constellationNames, onClick, isActive = false }: EventCardProps) {
  const cfg = STATUS_CONFIG[event.status] ?? STATUS_CONFIG['pending'] ?? { color: 'gray', label: 'Pending', icon: '○' };
  // Prefer backend-provided name; fall back to the map override (for custom labeling),
  // then a best-effort humanization of the raw constellation ID.
  const displayName =
    constellationNames[event.constellationId] ??
    event.constellationName ??
    event.constellationId.split('constellation-').join('').split('-').join(' ');

  const dateStr = formatDateRange(event.startedAt, event.lastActivityAt);

  return (
    <Paper
      p="sm"
      withBorder
      style={{
        cursor: 'pointer',
        borderLeft: isActive ? `3px solid var(--mantine-color-blue-5)` : undefined,
        transition: 'box-shadow 0.15s',
      }}
      onClick={() => onClick(event)}
    >
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Badge size="xs" color={cfg.color} variant="filled">
              {cfg.label}
            </Badge>
            <Text size="sm" fw={500} style={{ textTransform: 'capitalize' }}>
              {displayName}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {dateStr}
          </Text>
        </Group>

        <Group gap="xs" wrap="wrap">
          <Badge size="xs" color="gray" variant="outline">
            {event.taskCount} task{event.taskCount === 1 ? '' : 's'}
          </Badge>
          {event.taskTypes.map((t) => (
            <Badge key={t} size="xs" color="violet" variant="light">
              {t}
            </Badge>
          ))}
        </Group>

        {event.performerCount > 0 && (
          <Text size="xs" c="dimmed">
            {event.performerCount} staff member{event.performerCount === 1 ? '' : 's'}
          </Text>
        )}

        {event.isRegulated && (
          <Badge size="xs" color="orange" variant="light">
            Regulated
          </Badge>
        )}
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateRange(startedAt: string, lastActivityAt: string): string {
  const fmt = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const start = fmt(startedAt);
  const last = fmt(lastActivityAt);
  return start === last ? start : `${start} – ${last}`;
}
